import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import {
  adminBanUserAPI,
  deleteUserAPI,
  deleteUserGameIdAPI,
  fetchPaginatedUsersAPI,
} from "./usersAPI";

const initialState = {
  list: [],
  fetchStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  currentPage: 1,
  totalPages: 1,
  pageLimit: 10,
  error: null,
};

const mergeUpdatedUser = (user, updatedUser) => {
  if (user._id !== updatedUser._id) {
    return user;
  }

  return {
    ...user,
    ...updatedUser,
  };
};

export const fetchPaginatedUsers = createAsyncThunk(
  "users/fetchPaginatedUsers",
  async ({ page }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { users } = getState();
      const limit = users.pageLimit;

      const data = await fetchPaginatedUsersAPI(page, limit);

      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admins"
      );
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await deleteUserAPI(id);
      dispatch(
        showModal({
          message: `تم حذف ${data.data.name} بنجاح`,
          status: "success",
        })
      );
      return data.data._id;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء حذف اللاعب", status: "error" })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete admin"
      );
    }
  }
);

export const adminBanUser = createAsyncThunk(
  "users/adminBanUser",
  async ({ userId, banned }, { dispatch, rejectWithValue }) => {
    try {
      const data = await adminBanUserAPI(userId, banned);

      dispatch(
        showModal({
          message: banned
            ? `تم حظر اللاعب بنجاح`
            : `تم ازالة الحظر عن اللاعب بنجاح`,
          status: "success",
        })
      );
      return data.data;
    } catch (error) {
      console.log(error);
      dispatch(
        showModal({
          message: banned
            ? "حصل خطأ اثناء حظر اللاعب"
            : "حصل خطأ اثناء ازالة الحظر",
          status: "error",
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to update ban state"
      );
    }
  }
);

export const deleteUserGameId = createAsyncThunk(
  "users/deleteUserGameId",
  async ({ userId, gameIdDocId }, { dispatch, rejectWithValue }) => {
    try {
      await deleteUserGameIdAPI(gameIdDocId);

      dispatch(
        showModal({
          message: "تم حذف حساب اللعبة بنجاح",
          status: "success",
        })
      );
      return { userId, gameIdDocId };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء حذف حساب اللعبة",
          status: "error",
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete game id"
      );
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaginatedUsers.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchPaginatedUsers.fulfilled, (state, action) => {
        const newData = action.payload.data.data.filter(
          (item) => !state.list.find((existing) => existing._id === item._id)
        );
        state.fetchStatus = "succeeded";
        state.list = [...state.list, ...newData];
        state.currentPage = action.payload.data.page;
        state.totalPages = action.payload.data.totalPages;
      })
      .addCase(fetchPaginatedUsers.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(deleteUser.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.list = state.list.filter(({ _id }) => _id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(adminBanUser.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(adminBanUser.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.list = state.list.map((user) =>
          mergeUpdatedUser(user, action.payload)
        );
      })
      .addCase(adminBanUser.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload.error;
      })
      .addCase(deleteUserGameId.fulfilled, (state, action) => {
        const { userId, gameIdDocId } = action.payload;
        state.list = state.list.map((user) => {
          if (user._id !== userId || !Array.isArray(user.gameIds)) {
            return user;
          }

          return {
            ...user,
            gameIds: user.gameIds.filter(
              (gameId) => gameId?._id !== gameIdDocId
            ),
          };
        });
      });
  },
});

export default usersSlice.reducer;
